import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { BarCard, COLORS } from "@/components/charts/DashboardCharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { formatRupiah, formatDateID } from "@/lib/format";
import {
  Landmark, FileText, ShieldCheck, Play, RefreshCw, FileSpreadsheet,
  CheckCircle2, XCircle, Settings, BookOpen, ArrowUpRight, ArrowDownLeft,
  Calendar, ClipboardList, AlertCircle, BarChart, GitBranch,
} from "lucide-react";

import { FakturPenggantiDrillCard } from "@/pages/tax/Tahap5CGel3Pages";

const STATUS_COLORS = {
  DRAF: "bg-slate-500", VALID: "bg-emerald-600",
  SIAP_DIKIRIM: "bg-sky-600", DIKIRIM: "bg-blue-600",
  DITERIMA: "bg-emerald-700", DITOLAK: "bg-rose-600",
  DIBATALKAN: "bg-slate-400", PENGGANTI: "bg-amber-600",
};

function StatCard({ label, value, icon: Icon, tone = "slate", sub }) {
  const map = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    sky: "bg-sky-50 border-sky-200 text-sky-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    rose: "bg-rose-50 border-rose-200 text-rose-900",
    slate: "bg-slate-50 border-slate-200 text-slate-900",
  };
  return (
    <div className={`border rounded-md p-4 ${map[tone]}`} data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center gap-2 text-xs opacity-80"><Icon className="h-4 w-4" />{label}</div>
      <div className="font-mono-num text-lg font-semibold mt-1">{value}</div>
      {sub && <div className="text-xs opacity-70 mt-1">{sub}</div>}
    </div>
  );
}

// ============ DASHBOARD ============
export function TaxDashboard() {
  const [data, setData] = useState(null);
  const [coretax, setCoretax] = useState(null);
  const y = new Date().getFullYear();
  const [year, setYear] = useState(y);
  const [month, setMonth] = useState("");

  const load = async () => {
    try {
      const params = { year };
      if (month) params.month = parseInt(month);
      const [{ data: d }, { data: cs }] = await Promise.all([
        api.get("/tax/dashboard", { params }),
        api.get("/tax/coretax/status"),
      ]);
      setData(d); setCoretax(cs);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [year, month]);

  if (!data) return <div className="p-8 text-slate-500">Memuat dasbor pajak...</div>;

  return (
    <div className="space-y-6 page-fade" data-testid="tax-dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-slate-500 text-sm mb-1"><Landmark className="h-4 w-4" /><span>Perpajakan › Dasbor Pajak</span></div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Dasbor Pajak</h1>
          <p className="text-slate-500 mt-2">Ringkasan posisi PPN, PPh, dan status kesiapan Coretax.</p>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-sm">Tahun</Label>
          <Input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value) || y)} className="w-24" data-testid="tax-year-filter" />
          <Label className="text-sm ml-2">Bulan</Label>
          <Select value={month} onValueChange={(v) => setMonth(v === "all" ? "" : v)}>
            <SelectTrigger className="w-28" data-testid="tax-month-filter"><SelectValue placeholder="Semua" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua</SelectItem>
              {Array.from({length:12}).map((_, i) => <SelectItem key={i+1} value={String(i+1)}>{String(i+1).padStart(2,"0")}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Coretax banner */}
      <Card className={coretax?.mode === "API" && !coretax?.api_configured ? "border-amber-300 bg-amber-50/40" : "border-slate-200"}>
        <CardContent className="p-4 flex items-center gap-3 flex-wrap">
          <AlertCircle className={`h-5 w-5 ${coretax?.mode === "API" && !coretax?.api_configured ? "text-amber-600" : "text-slate-500"}`} />
          <div className="flex-1">
            <div className="text-sm font-semibold" data-testid="coretax-mode-label">Coretax Mode: <Badge>{coretax?.mode}</Badge></div>
            <div className="text-xs text-slate-600 mt-0.5">{coretax?.message}</div>
          </div>
          <Link to="/perpajakan/profil"><Button variant="outline" size="sm" data-testid="coretax-profile-btn"><Settings className="h-4 w-4 mr-1" />Konfigurasi</Button></Link>
        </CardContent>
      </Card>

      {/* PPN Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard label="PPN Keluaran (Faktur)" value={formatRupiah(data.ppn_keluaran.ppn)} icon={ArrowUpRight} tone="emerald" sub={`${data.ppn_keluaran.count} faktur · DPP ${formatRupiah(data.ppn_keluaran.dpp)}`} />
        <StatCard label="PPN Masukan (Faktur)" value={formatRupiah(data.ppn_masukan.ppn)} icon={ArrowDownLeft} tone="sky" sub={`${data.ppn_masukan.count} faktur · DPP ${formatRupiah(data.ppn_masukan.dpp)}`} />
        <StatCard label="Estimasi PPN Kurang Bayar" value={formatRupiah(data.estimasi_ppn_kurang_bayar)} icon={ClipboardList} tone={data.estimasi_ppn_kurang_bayar > 0 ? "amber" : "emerald"} />
        <StatCard label="Faktur Draf" value={data.faktur_draft} icon={FileText} tone={data.faktur_draft > 0 ? "amber" : "slate"} sub={`${data.faktur_perlu_ditinjau} perlu ditinjau`} />
      </div>

      {/* Phase 1 chart — PPN Masukan vs Keluaran (DPP vs PPN) + GL vs Faktur */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <BarCard
          title="PPN Keluaran vs Masukan (Faktur)"
          testId="chart-ppn-faktur"
          currency
          hint="Belum ada faktur PPN pada periode terpilih."
          data={[
            { name: "DPP",  Keluaran: data.ppn_keluaran?.dpp || 0, Masukan: data.ppn_masukan?.dpp || 0 },
            { name: "PPN",  Keluaran: data.ppn_keluaran?.ppn || 0, Masukan: data.ppn_masukan?.ppn || 0 },
          ]}
          bars={[
            { key: "Keluaran", label: "Keluaran", color: COLORS.positive },
            { key: "Masukan",  label: "Masukan",  color: COLORS.info },
          ]}
        />
        <BarCard
          title="PPN Faktur vs GL"
          testId="chart-ppn-gl"
          currency
          hint="Belum ada data PPN GL pada periode terpilih."
          data={[
            { name: "Keluaran", Faktur: data.ppn_keluaran?.ppn || 0, GL: data.ppn_output_gl || 0 },
            { name: "Masukan",  Faktur: data.ppn_masukan?.ppn  || 0, GL: data.ppn_input_gl  || 0 },
          ]}
          bars={[
            { key: "Faktur", label: "Faktur", color: COLORS.baseline },
            { key: "GL",     label: "GL",     color: COLORS.warning },
          ]}
        />
      </div>

      {/* Quick links */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          ["/perpajakan/ppn", "PPN Hub", "Pajak Keluaran & Masukan.", FileSpreadsheet],
          ["/perpajakan/faktur", "Faktur Pajak", "Kelola faktur & status.", FileText],
          ["/perpajakan/pph", "PPh (Withholding)", "PPh 21/22/23/25/Final + Bukti Potong.", ArrowDownLeft],
          ["/perpajakan/pembayaran", "Pembayaran Pajak", "Kewajiban & pembayaran + Bank Match.", ClipboardList],
          ["/perpajakan/kalender", "Kalender Pajak", "Deadline & reminder.", Calendar],
          ["/perpajakan/kepatuhan", "Compliance Wall", "Cek kelengkapan data pajak.", AlertCircle],
          ["/perpajakan/rekonsiliasi", "Rekonsiliasi Pajak", "PPh vs GL, Payment vs Bank.", ShieldCheck],
          ["/perpajakan/integritas", "Pusat Integritas Pajak", "PPN vs GL, Faktur vs Invoice.", ShieldCheck],
          ["/perpajakan/dasbor-eksekutif", "Dasbor Eksekutif Pajak", "Ringkasan PPN, PPh×5, pembayaran, SPT, integritas.", BarChart],
          ["/perpajakan/coretax-io", "Coretax Import/Export", "Template CSV · Preview validasi · Commit atomic · Match faktur.", FileSpreadsheet],
          ["/perpajakan/spt", "Kertas Kerja SPT", "SPT Masa PPN, PPh, workflow & drill-down.", ClipboardList],
          ["/perpajakan/arsip", "Arsip Dokumen Pajak", "Registry faktur, bukti potong, setor, NTPN, SPT.", FileText],
          ["/perpajakan/coretax-center", "Coretax Center", "Mode Coretax, sandbox simulator, compliance wall.", Settings],
          ["/perpajakan/integritas-final", "Pusat Integritas Final", "10-way rekonsiliasi & compliance blocker.", ShieldCheck],
          ["/perpajakan/profil", "Profil & Aturan Pajak", "Tax profile & versioned rules.", BookOpen],
        ].map(([p, t, d, I]) => (
          <Card key={p} className="hover:border-slate-400 transition"><CardContent className="p-4">
            <div className="flex items-center gap-2"><I className="h-5 w-5 text-slate-700" /><div className="font-display font-semibold">{t}</div></div>
            <p className="text-sm text-slate-500 mt-1.5">{d}</p>
            <Link to={p}><Button size="sm" variant="outline" className="mt-3" data-testid={`nav-${p.replace(/\//g,'-')}`}>Buka</Button></Link>
          </CardContent></Card>
        ))}
      </div>
    </div>
  );
}

// ============ PPN Hub ============
export function TaxPPNHub() {
  const [tab, setTab] = useState("keluaran");
  const [keluaran, setKeluaran] = useState([]);
  const [masukan, setMasukan] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [k, m] = await Promise.all([
        api.get("/tax/faktur", { params: { faktur_type: "KELUARAN" } }),
        api.get("/tax/faktur", { params: { faktur_type: "MASUKAN" } }),
      ]);
      setKeluaran(k.data); setMasukan(m.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const generateAll = async () => {
    try {
      const { data } = await api.post("/tax/faktur/generate-from-invoices");
      toast.success(`Dibuat: ${data.count_keluaran} Keluaran + ${data.count_masukan} Masukan`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const bulkValidate = async () => {
    try {
      const { data } = await api.post("/tax/faktur/bulk-validate");
      toast.success(`${data.validated} faktur divalidasi`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const exportCsv = async (faktur_type) => {
    try {
      const res = await api.get("/tax/coretax/export/csv", { params: { faktur_type }, responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `coretax_${faktur_type}.csv`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("CSV Coretax berhasil diunduh");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const Row = ({ f, type }) => (
    <TableRow>
      <TableCell className="font-mono-num text-xs">{f.faktur_number}</TableCell>
      <TableCell>{formatDateID(f.faktur_date)}</TableCell>
      <TableCell>{type === "KELUARAN" ? f.customer_name : f.supplier_name}</TableCell>
      <TableCell className="font-mono-num text-xs">{type === "KELUARAN" ? f.customer_npwp || "-" : f.supplier_npwp || "-"}</TableCell>
      <TableCell className="font-mono-num text-xs">{type === "KELUARAN" ? f.invoice_number : f.bill_number}</TableCell>
      <TableCell className="text-right font-mono-num">{formatRupiah(f.dpp)}</TableCell>
      <TableCell className="text-right font-mono-num">{formatRupiah(f.ppn)}</TableCell>
      <TableCell><Badge className={STATUS_COLORS[f.status] || "bg-slate-500"}>{f.status}</Badge></TableCell>
      <TableCell className="text-xs">{f.coretax_status || "BELUM_DIPROSES"}</TableCell>
    </TableRow>
  );

  return (
    <div className="space-y-6 page-fade" data-testid="tax-ppn-hub-page">
      <div>
        <div className="flex items-center gap-2 text-slate-500 text-sm mb-1"><FileSpreadsheet className="h-4 w-4" /><span>Perpajakan › PPN</span></div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">PPN Hub</h1>
        <p className="text-slate-500 mt-2">Daftar Faktur Pajak Keluaran & Masukan yang tersinkron dengan Invoice/Tagihan Tahap 3.</p>
      </div>

      <Card><CardContent className="p-4 flex flex-wrap gap-2">
        <Button onClick={generateAll} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="generate-faktur-btn"><RefreshCw className="h-4 w-4 mr-1.5" />Generate dari Invoice/Bill</Button>
        <Button onClick={bulkValidate} variant="outline" data-testid="bulk-validate-btn"><CheckCircle2 className="h-4 w-4 mr-1.5" />Bulk Validate Draf</Button>
        <Button onClick={() => exportCsv("KELUARAN")} variant="outline" data-testid="export-keluaran-csv"><FileText className="h-4 w-4 mr-1.5" />Export CSV Keluaran</Button>
        <Button onClick={() => exportCsv("MASUKAN")} variant="outline" data-testid="export-masukan-csv"><FileText className="h-4 w-4 mr-1.5" />Export CSV Masukan</Button>
      </CardContent></Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="ppn-tabs">
          <TabsTrigger value="keluaran" data-testid="tab-keluaran">Pajak Keluaran ({keluaran.length})</TabsTrigger>
          <TabsTrigger value="masukan" data-testid="tab-masukan">Pajak Masukan ({masukan.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="keluaran">
          <Card><CardContent className="p-4">
            <Table><TableHeader><TableRow><TableHead>Nomor Faktur</TableHead><TableHead>Tanggal</TableHead><TableHead>Pelanggan</TableHead><TableHead>NPWP</TableHead><TableHead>Referensi Invoice</TableHead><TableHead className="text-right">DPP</TableHead><TableHead className="text-right">PPN</TableHead><TableHead>Status</TableHead><TableHead>Coretax</TableHead></TableRow></TableHeader>
              <TableBody>{loading ? <TableRow><TableCell colSpan={9} className="text-center py-4 text-slate-500">Memuat...</TableCell></TableRow> : keluaran.length === 0 ? <TableRow><TableCell colSpan={9} className="text-center py-4 text-slate-500">Belum ada faktur keluaran.</TableCell></TableRow> : keluaran.map((f) => <Row key={f.id} f={f} type="KELUARAN" />)}</TableBody></Table>
          </CardContent></Card>
        </TabsContent>
        <TabsContent value="masukan">
          <Card><CardContent className="p-4">
            <Table><TableHeader><TableRow><TableHead>Nomor Faktur</TableHead><TableHead>Tanggal</TableHead><TableHead>Pemasok</TableHead><TableHead>NPWP</TableHead><TableHead>Referensi Bill</TableHead><TableHead className="text-right">DPP</TableHead><TableHead className="text-right">PPN</TableHead><TableHead>Status</TableHead><TableHead>Coretax</TableHead></TableRow></TableHeader>
              <TableBody>{loading ? <TableRow><TableCell colSpan={9} className="text-center py-4 text-slate-500">Memuat...</TableCell></TableRow> : masukan.length === 0 ? <TableRow><TableCell colSpan={9} className="text-center py-4 text-slate-500">Belum ada faktur masukan.</TableCell></TableRow> : masukan.map((f) => <Row key={f.id} f={f} type="MASUKAN" />)}</TableBody></Table>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ============ Faktur Detail / Actions ============
export function TaxFakturPage() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [filter, setFilter] = useState({ type: "all", status: "all" });
  const [drillId, setDrillId] = useState(null);

  const load = async () => {
    try {
      const params = {};
      if (filter.type !== "all") params.faktur_type = filter.type;
      if (filter.status !== "all") params.status = filter.status;
      const { data } = await api.get("/tax/faktur", { params });
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [filter]);

  const doAction = async (fid, action) => {
    const note = ["MARK_REJECTED", "CANCEL"].includes(action) ? window.prompt("Alasan:") : "";
    if (["MARK_REJECTED", "CANCEL"].includes(action) && !note) return;
    try {
      await api.post(`/tax/faktur/${fid}/status`, { action, note });
      toast.success(`Faktur ${action}`);
      await load(); setSelected(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const buatPengganti = async (fid) => {
    const reason = window.prompt("Alasan penerbitan faktur pengganti:");
    if (!reason) return;
    try {
      const { data } = await api.post("/tax/faktur/pengganti", { original_id: fid, reason });
      toast.success(`Faktur pengganti ${data.faktur_number} dibuat`);
      await load(); setSelected(null);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6 page-fade" data-testid="tax-faktur-page">
      <div>
        <div className="flex items-center gap-2 text-slate-500 text-sm mb-1"><FileText className="h-4 w-4" /><span>Perpajakan › Faktur Pajak</span></div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Faktur Pajak</h1>
        <p className="text-slate-500 mt-2">Kelola lifecycle Faktur Pajak (DRAF → VALID → SIAP_DIKIRIM → DIKIRIM → DITERIMA/DITOLAK).</p>
      </div>

      <Card><CardContent className="p-4 flex flex-wrap gap-3">
        <div><Label className="text-xs">Jenis</Label>
          <Select value={filter.type} onValueChange={(v) => setFilter({ ...filter, type: v })}>
            <SelectTrigger className="w-40" data-testid="faktur-type-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Semua</SelectItem><SelectItem value="KELUARAN">Keluaran</SelectItem><SelectItem value="MASUKAN">Masukan</SelectItem></SelectContent>
          </Select>
        </div>
        <div><Label className="text-xs">Status</Label>
          <Select value={filter.status} onValueChange={(v) => setFilter({ ...filter, status: v })}>
            <SelectTrigger className="w-40" data-testid="faktur-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Semua</SelectItem><SelectItem value="DRAF">Draf</SelectItem><SelectItem value="VALID">Valid</SelectItem><SelectItem value="SIAP_DIKIRIM">Siap Dikirim</SelectItem><SelectItem value="DIKIRIM">Dikirim</SelectItem><SelectItem value="DITERIMA">Diterima</SelectItem><SelectItem value="DITOLAK">Ditolak</SelectItem><SelectItem value="DIBATALKAN">Dibatalkan</SelectItem><SelectItem value="PENGGANTI">Pengganti</SelectItem></SelectContent>
          </Select>
        </div>
      </CardContent></Card>

      <Card><CardContent className="p-0">
        <Table><TableHeader><TableRow><TableHead>Faktur</TableHead><TableHead>Jenis</TableHead><TableHead>Tanggal</TableHead><TableHead>Pihak</TableHead><TableHead className="text-right">DPP</TableHead><TableHead className="text-right">PPN</TableHead><TableHead>Status</TableHead><TableHead>Coretax</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
          <TableBody>{items.length === 0 ? <TableRow><TableCell colSpan={9} className="text-center py-6 text-slate-500">Tidak ada faktur.</TableCell></TableRow> : items.map((f) => (
            <TableRow key={f.id}>
              <TableCell className="font-mono-num text-xs">{f.faktur_number}</TableCell>
              <TableCell><Badge variant="outline">{f.faktur_type}</Badge></TableCell>
              <TableCell>{formatDateID(f.faktur_date)}</TableCell>
              <TableCell>{f.customer_name || f.supplier_name}</TableCell>
              <TableCell className="text-right font-mono-num">{formatRupiah(f.dpp)}</TableCell>
              <TableCell className="text-right font-mono-num">{formatRupiah(f.ppn)}</TableCell>
              <TableCell><Badge className={STATUS_COLORS[f.status] || "bg-slate-500"}>{f.status}</Badge></TableCell>
              <TableCell className="text-xs">{f.coretax_status}</TableCell>
              <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={() => setSelected(f)} data-testid={`faktur-actions-${f.id}`}>Aksi</Button>{(f.original_faktur_id || f.pengganti_delta_ppn !== undefined) && <Button size="sm" variant="ghost" onClick={() => setDrillId(f.id)} data-testid={`faktur-drill-${f.id}`}><GitBranch className="h-3 w-3" /></Button>}</TableCell>
            </TableRow>
          ))}</TableBody></Table>
      </CardContent></Card>

      {selected && (
        <Card className="border-emerald-300 border-2"><CardContent className="p-5">
          <div className="font-display font-semibold mb-3">Aksi Faktur {selected.faktur_number} ({selected.status})</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {selected.status === "DRAF" && <Button onClick={() => doAction(selected.id, "VALIDATE")} className="bg-emerald-600 text-white" data-testid="action-validate">Validasi</Button>}
            {selected.status === "VALID" && <Button onClick={() => doAction(selected.id, "MARK_READY")} data-testid="action-ready">Tandai Siap Dikirim</Button>}
            {selected.status === "SIAP_DIKIRIM" && <Button onClick={() => doAction(selected.id, "MARK_SENT")} data-testid="action-sent">Tandai Dikirim (Simulasi)</Button>}
            {selected.status === "DIKIRIM" && <Button onClick={() => doAction(selected.id, "MARK_ACCEPTED")} className="bg-emerald-600 text-white" data-testid="action-accepted">Tandai Diterima</Button>}
            {selected.status === "DIKIRIM" && <Button onClick={() => doAction(selected.id, "MARK_REJECTED")} variant="destructive" data-testid="action-rejected">Tandai Ditolak</Button>}
            {!["DIBATALKAN", "PENGGANTI"].includes(selected.status) && <Button onClick={() => buatPengganti(selected.id)} variant="outline" data-testid="action-pengganti">Buat Faktur Pengganti</Button>}
            <Button onClick={() => setDrillId(selected.id)} variant="outline" data-testid="action-drill"><GitBranch className="h-4 w-4 mr-1" />Drill-down Pengganti</Button>
            {!["DIBATALKAN", "PENGGANTI"].includes(selected.status) && <Button onClick={() => doAction(selected.id, "CANCEL")} variant="destructive" data-testid="action-cancel">Batalkan</Button>}
            <Button onClick={() => setSelected(null)} variant="ghost" data-testid="action-close">Tutup</Button>
          </div>
          {selected.tax_snapshot && (
            <div className="mt-4 p-3 bg-slate-50 rounded border">
              <div className="text-xs font-semibold mb-1">Tax Snapshot (immutable)</div>
              <div className="text-xs font-mono-num text-slate-600">
                Rule {selected.tax_snapshot.rule_code} v{selected.tax_snapshot.rule_version} · Rate {selected.tax_snapshot.rate}% · DPP {formatRupiah(selected.tax_snapshot.dpp)} · Pajak {formatRupiah(selected.tax_snapshot.tax_amount)} · Effective {selected.tax_snapshot.effective_date}
              </div>
            </div>
          )}
        </CardContent></Card>
      )}
      {drillId && <FakturPenggantiDrillCard fakturId={drillId} onClose={() => setDrillId(null)} />}
    </div>
  );
}

// ============ Integrity ============
export function TaxIntegrityPage() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/tax/integrity").then(({ data }) => setD(data)).catch((e) => toast.error(formatApiError(e))); }, []);
  const badgeCls = (s) => s === "SESUAI" ? "bg-emerald-600" : s === "SELISIH" ? "bg-rose-600" : s === "PERLU_DITINJAU" ? "bg-amber-600" : "bg-slate-400";

  if (!d) return <div className="p-8 text-slate-500">Memuat integritas pajak...</div>;

  const rows = [
    { key: "ppn_keluaran", label: "PPN Keluaran (Faktur vs GL)" },
    { key: "ppn_masukan", label: "PPN Masukan (Faktur vs GL, Creditable)" },
    { key: "faktur_vs_invoice", label: "Faktur vs Invoice Tahap 3" },
    { key: "pph_21", label: "PPh 21 (belum ada data)" },
    { key: "pph_22", label: "PPh 22 (belum ada data)" },
    { key: "pph_23", label: "PPh 23 (belum ada data)" },
    { key: "pph_25", label: "PPh 25 (belum ada data)" },
    { key: "pph_final", label: "PPh Final (belum ada data)" },
  ];

  return (
    <div className="space-y-6 page-fade" data-testid="tax-integrity-page">
      <div>
        <div className="flex items-center gap-2 text-slate-500 text-sm mb-1"><ShieldCheck className="h-4 w-4" /><span>Perpajakan › Pusat Integritas Pajak</span></div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Pusat Integritas Pajak</h1>
        <p className="text-slate-500 mt-2">Cek kesesuaian Tax Subledger vs General Ledger.</p>
      </div>
      <Card><CardContent className="p-0">
        <Table><TableHeader><TableRow><TableHead>Cek</TableHead><TableHead className="text-right">Nilai 1</TableHead><TableHead className="text-right">Nilai 2</TableHead><TableHead className="text-right">Selisih</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
          <TableBody>{rows.map((r) => {
            const v = d[r.key];
            const a = v.tax_subledger ?? v.invoice_tax ?? 0;
            const b = v.gl ?? v.faktur_count ?? 0;
            return (
              <TableRow key={r.key} data-testid={`integrity-row-${r.key}`}>
                <TableCell>{r.label}</TableCell>
                <TableCell className="text-right font-mono-num">{formatRupiah(a)}</TableCell>
                <TableCell className="text-right font-mono-num">{r.key === "faktur_vs_invoice" ? b : formatRupiah(b)}</TableCell>
                <TableCell className="text-right font-mono-num">{formatRupiah(v.difference)}</TableCell>
                <TableCell><Badge className={badgeCls(v.status)}>{v.status}</Badge></TableCell>
              </TableRow>
            );
          })}</TableBody></Table>
      </CardContent></Card>
    </div>
  );
}

// ============ Profile & Rules ============
export function TaxProfilePage() {
  const [profile, setProfile] = useState(null);
  const [rules, setRules] = useState([]);
  const [form, setForm] = useState(null);
  const [ruleForm, setRuleForm] = useState({ code: "", name: "", tax_type: "PPN", rate: 11, dpp_method: "NILAI_TRANSAKSI", dpp_factor: 1, transaction_code: "04", output_or_input: "OUTPUT", effective_date: "2026-01-01", legal_basis: "" });
  const [showRuleForm, setShowRuleForm] = useState(false);
  const [simulate, setSimulate] = useState({ tax_type: "PPN", tax_code: "PPN-11", transaction_date: "2024-01-01", amount: 1000000 });
  const [simulateResult, setSimulateResult] = useState(null);

  const load = async () => {
    try {
      const [p, r] = await Promise.all([api.get("/tax/profile"), api.get("/tax/rules")]);
      setProfile(p.data); setForm(p.data); setRules(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const saveProfile = async () => {
    try { await api.put("/tax/profile", form); toast.success("Profil pajak tersimpan"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const createRule = async () => {
    try { await api.post("/tax/rules", ruleForm); toast.success("Rule berhasil dibuat (status DRAF)"); setShowRuleForm(false); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const approveRule = async (id) => {
    try { await api.post(`/tax/rules/${id}/approve`); toast.success("Rule disetujui & AKTIF"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const deactivateRule = async (id) => {
    if (!window.confirm("Nonaktifkan rule ini?")) return;
    try { await api.post(`/tax/rules/${id}/deactivate`); toast.success("Rule dinonaktifkan"); await load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const runSimulate = async () => {
    try {
      const { data } = await api.post("/tax/rules/simulate", simulate);
      setSimulateResult(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!profile) return <div className="p-8 text-slate-500">Memuat...</div>;

  return (
    <div className="space-y-6 page-fade" data-testid="tax-profile-page">
      <div>
        <div className="flex items-center gap-2 text-slate-500 text-sm mb-1"><BookOpen className="h-4 w-4" /><span>Perpajakan › Profil & Aturan</span></div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Profil & Aturan Pajak</h1>
      </div>

      <Tabs defaultValue="profile">
        <TabsList data-testid="profile-rules-tabs">
          <TabsTrigger value="profile" data-testid="tab-profile">Profil Perpajakan</TabsTrigger>
          <TabsTrigger value="rules" data-testid="tab-rules">Aturan Pajak ({rules.length})</TabsTrigger>
          <TabsTrigger value="simulate" data-testid="tab-simulate">Simulasi Rule</TabsTrigger>
        </TabsList>
        <TabsContent value="profile">
          <Card><CardContent className="p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {["company_name","npwp","nitku","pkp_status","pkp_date","klu","tax_address","kpp","reporting_currency","pic_name","pic_email","pic_phone"].map((k) => (
              <div key={k}><Label className="text-xs uppercase">{k.replace(/_/g," ")}</Label>
                <Input value={form?.[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`profile-${k}`} />
              </div>
            ))}
            <div><Label className="text-xs">Coretax Mode</Label>
              <Select value={form?.coretax_mode || "MANUAL"} onValueChange={(v) => setForm({ ...form, coretax_mode: v })}>
                <SelectTrigger data-testid="profile-coretax-mode"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="MANUAL">Manual</SelectItem><SelectItem value="IMPORT_EXPORT">Import/Export</SelectItem><SelectItem value="API">API</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="col-span-full"><Button onClick={saveProfile} className="bg-slate-900 text-white" data-testid="save-profile-btn">Simpan Profil</Button></div>
          </CardContent></Card>
        </TabsContent>
        <TabsContent value="rules">
          <Card><CardContent className="p-4">
            <div className="flex justify-between items-center mb-3">
              <div className="font-display font-semibold">Aturan Pajak (Versioned)</div>
              <Button onClick={() => setShowRuleForm(!showRuleForm)} data-testid="show-rule-form-btn">+ Rule Baru</Button>
            </div>
            {showRuleForm && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3 p-3 border rounded bg-slate-50">
                <div><Label className="text-xs">Kode</Label><Input value={ruleForm.code} onChange={(e) => setRuleForm({...ruleForm, code: e.target.value})} data-testid="rule-code" /></div>
                <div><Label className="text-xs">Nama</Label><Input value={ruleForm.name} onChange={(e) => setRuleForm({...ruleForm, name: e.target.value})} data-testid="rule-name" /></div>
                <div><Label className="text-xs">Jenis</Label>
                  <Select value={ruleForm.tax_type} onValueChange={(v) => setRuleForm({...ruleForm, tax_type: v})}>
                    <SelectTrigger data-testid="rule-tax-type"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="PPN">PPN</SelectItem><SelectItem value="PPh21">PPh 21</SelectItem><SelectItem value="PPh22">PPh 22</SelectItem><SelectItem value="PPh23">PPh 23</SelectItem><SelectItem value="PPh25">PPh 25</SelectItem><SelectItem value="PPhFinal">PPh Final</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Tarif (%)</Label><Input type="number" step="0.01" value={ruleForm.rate} onChange={(e) => setRuleForm({...ruleForm, rate: parseFloat(e.target.value)||0})} data-testid="rule-rate" /></div>
                <div><Label className="text-xs">DPP Method</Label>
                  <Select value={ruleForm.dpp_method} onValueChange={(v) => setRuleForm({...ruleForm, dpp_method: v})}>
                    <SelectTrigger data-testid="rule-dpp-method"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="NILAI_TRANSAKSI">Nilai Transaksi</SelectItem><SelectItem value="NILAI_LAIN">Nilai Lain</SelectItem><SelectItem value="KHUSUS">Khusus</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">DPP Factor</Label><Input type="number" step="0.0001" value={ruleForm.dpp_factor} onChange={(e) => setRuleForm({...ruleForm, dpp_factor: parseFloat(e.target.value)||1})} data-testid="rule-dpp-factor" /></div>
                <div><Label className="text-xs">Berlaku</Label><Input type="date" value={ruleForm.effective_date} onChange={(e) => setRuleForm({...ruleForm, effective_date: e.target.value})} data-testid="rule-effective" /></div>
                <div><Label className="text-xs">Dasar Hukum</Label><Input value={ruleForm.legal_basis} onChange={(e) => setRuleForm({...ruleForm, legal_basis: e.target.value})} data-testid="rule-legal" /></div>
                <div><Button onClick={createRule} className="bg-emerald-600 text-white mt-4" data-testid="create-rule-btn">Simpan Rule</Button></div>
              </div>
            )}
            <Table><TableHeader><TableRow><TableHead>Kode</TableHead><TableHead>Nama</TableHead><TableHead>Jenis</TableHead><TableHead className="text-right">Tarif</TableHead><TableHead>DPP</TableHead><TableHead>Berlaku</TableHead><TableHead>Berakhir</TableHead><TableHead>Versi</TableHead><TableHead>Status</TableHead><TableHead>Aksi</TableHead></TableRow></TableHeader>
              <TableBody>{rules.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-mono-num">{r.code}</TableCell>
                  <TableCell>{r.name}</TableCell>
                  <TableCell><Badge variant="outline">{r.tax_type}</Badge></TableCell>
                  <TableCell className="text-right font-mono-num">{r.rate}%</TableCell>
                  <TableCell className="text-xs">{r.dpp_method} × {r.dpp_factor}</TableCell>
                  <TableCell>{r.effective_date}</TableCell>
                  <TableCell>{r.expiry_date || "-"}</TableCell>
                  <TableCell>v{r.version}</TableCell>
                  <TableCell><Badge className={r.status === "AKTIF" ? "bg-emerald-600" : r.status === "DRAF" ? "bg-amber-500" : "bg-slate-400"}>{r.status}</Badge></TableCell>
                  <TableCell>
                    {r.status === "DRAF" && <Button size="sm" variant="outline" onClick={() => approveRule(r.id)} data-testid={`approve-rule-${r.id}`}>Setujui</Button>}
                    {r.status === "AKTIF" && <Button size="sm" variant="ghost" onClick={() => deactivateRule(r.id)} data-testid={`deactivate-rule-${r.id}`}>Nonaktif</Button>}
                  </TableCell>
                </TableRow>
              ))}</TableBody></Table>
          </CardContent></Card>
        </TabsContent>
        <TabsContent value="simulate">
          <Card><CardContent className="p-5 space-y-3">
            <div className="font-display font-semibold">Simulasi Perhitungan Tax Rule</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div><Label className="text-xs">Jenis</Label>
                <Select value={simulate.tax_type} onValueChange={(v) => setSimulate({...simulate, tax_type: v})}>
                  <SelectTrigger data-testid="sim-tax-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="PPN">PPN</SelectItem><SelectItem value="PPh23">PPh 23</SelectItem><SelectItem value="PPhFinal">PPh Final</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Kode Rule</Label><Input value={simulate.tax_code} onChange={(e) => setSimulate({...simulate, tax_code: e.target.value})} data-testid="sim-code" /></div>
              <div><Label className="text-xs">Tanggal Transaksi</Label><Input type="date" value={simulate.transaction_date} onChange={(e) => setSimulate({...simulate, transaction_date: e.target.value})} data-testid="sim-date" /></div>
              <div><Label className="text-xs">Nilai</Label><Input type="number" value={simulate.amount} onChange={(e) => setSimulate({...simulate, amount: parseFloat(e.target.value)||0})} data-testid="sim-amount" /></div>
            </div>
            <Button onClick={runSimulate} className="bg-slate-900 text-white" data-testid="run-sim-btn"><Play className="h-4 w-4 mr-1.5" />Simulasikan</Button>
            {simulateResult && (
              <div className="mt-3 p-3 border rounded bg-slate-50">
                {simulateResult.success ? (
                  <div className="text-sm space-y-1">
                    <div className="font-semibold">Snapshot Result:</div>
                    <pre className="text-xs font-mono-num">{JSON.stringify(simulateResult.snapshot, null, 2)}</pre>
                  </div>
                ) : (
                  <div className="text-rose-700 text-sm">{simulateResult.detail}</div>
                )}
              </div>
            )}
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
